import { supabase } from '@/integrations/supabase/client';
import { azureCircuitBreaker } from '@/lib/security/circuitBreaker';
import { validateInput, azureRequestSchema } from '@/lib/validation/schemas';

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface PremiumTextResponse {
  content: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Calls Azure OpenAI premium model for text generation
 * @param messages - Array of chat messages
 * @param deployment - Optional deployment name (defaults to gpt-5-premium)
 * @returns Promise<string> - Generated text content
 */
export async function callPremiumText(
  messages: Message[], 
  deployment: string = 'gpt-5-premium'
): Promise<string> {
  // Input validation
  const validated = validateInput(azureRequestSchema, {
    messages,
    deployment
  });

  const controller = new AbortController();
  const timeoutMs = 120000; // 120 seconds

  const timeout = setTimeout(() => {
    console.error('❌ [callPremiumText] TIMEOUT after 120 seconds - aborting call');
    controller.abort();
  }, timeoutMs);

  try {
    // Execute with circuit breaker protection
    return await azureCircuitBreaker.execute(async () => {
      console.log('🔄 [callPremiumText] Anropar edge function azure-openai-premium...', {
        deployment: validated.deployment,
        messageCount: validated.messages.length,
        timeout: `${timeoutMs / 1000}s`
      });

      const startTime = Date.now();
      const { data, error } = await supabase.functions.invoke('azure-openai-premium', {
        body: { 
          messages: validated.messages, 
          deployment: validated.deployment 
        }
      });
      
      clearTimeout(timeout);
      const elapsedMs = Date.now() - startTime;
      
      console.log('✅ [callPremiumText] Edge function svarade:', {
        hasData: !!data,
        hasError: !!error,
        elapsedMs
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data) {
        throw new Error('No response data received from edge function');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return data.content;
    });
  } catch (error) {
    clearTimeout(timeout);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ [callPremiumText] Request TIMEOUT efter 120 sekunder');
      throw new Error('Azure OpenAI timeout efter 120 sekunder');
    }
    
    console.error('❌ [callPremiumText] Unexpected error:', {
      message: error instanceof Error ? error.message : String(error),
      type: error?.constructor?.name
    });
    throw error;
  }
}

/**
 * Calls Azure OpenAI premium model and returns full response with usage data
 * @param messages - Array of chat messages
 * @param deployment - Optional deployment name (defaults to gpt-5-premium)
 * @returns Promise<PremiumTextResponse> - Full response with content and usage
 */
export async function callPremiumTextWithUsage(
  messages: Message[], 
  deployment: string = 'gpt-5-premium'
): Promise<PremiumTextResponse> {
  // P0-3: Add timeout handling (120 seconds)
  const controller = new AbortController();
  const timeoutMs = 120000;
  const timeout = setTimeout(() => {
    console.error('❌ [callPremiumTextWithUsage] TIMEOUT efter 120 sekunder');
    controller.abort();
  }, timeoutMs);

  try {
    console.log('🔄 [callPremiumTextWithUsage] Anropar edge function...', {
      deployment,
      messageCount: messages.length
    });

    const startTime = Date.now();
    const { data, error } = await supabase.functions.invoke('azure-openai-premium', {
      body: { messages, deployment }
    });
    
    clearTimeout(timeout);
    const elapsedMs = Date.now() - startTime;
    
    console.log('✅ [callPremiumTextWithUsage] Svar mottaget:', {
      hasData: !!data,
      elapsedMs
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(`Edge function error: ${error.message}`);
    }

    if (!data) {
      throw new Error('No response data received from edge function');
    }

    if (data.error) {
      throw new Error(data.error);
    }

    return {
      content: data.content,
      usage: data.usage
    };
  } catch (error) {
    clearTimeout(timeout);
    
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('❌ [callPremiumTextWithUsage] Request TIMEOUT efter 120 sekunder');
      throw new Error('Azure OpenAI timeout efter 120 sekunder');
    }
    
    console.error('❌ [callPremiumTextWithUsage] Unexpected error:', error);
    throw error;
  }
}